import { useState, useEffect, useCallback } from "react";
import { Search, RefreshCw, CheckCircle, AlertTriangle, Info } from "lucide-react";
import {
  fetchCounties,
  lookupTaxData,
  applyTaxToOS,
  type TaxAssessment,
  type CountyInfo,
} from "../../api/client";
import { fmtCurrency } from "../../utils/format";
import { useToast } from "../../contexts/ToastContext";
import TaxAssessmentCard from "./TaxAssessmentCard";

/** Detect FL county from an address string using city/keyword matching. */
function detectCountyFromAddress(address: string | undefined | null): number | null {
  if (!address) return null;
  const lower = address.toLowerCase();
  // Map of FL city/area keywords to county FDOR codes
  const cityCountyMap: [string, number][] = [
    ["fort lauderdale", 6], ["ft lauderdale", 6], ["ft. lauderdale", 6],
    ["hollywood", 6], ["pompano", 6], ["deerfield", 6], ["coral springs", 6],
    ["plantation", 6], ["davie", 6], ["sunrise", 6], ["weston", 6],
    ["pembroke", 6], ["miramar", 6], ["coconut creek", 6], ["lauderhill", 6],
    ["tamarac", 6], ["margate", 6], ["dania", 6], ["hallandale", 6],
    ["broward", 6],
    ["miami", 13], ["doral", 13], ["hialeah", 13], ["homestead", 13],
    ["kendall", 13], ["coral gables", 13], ["aventura", 13], ["miami-dade", 13],
    ["key biscayne", 13], ["north miami", 13], ["south miami", 13],
    ["miami beach", 13], ["miami gardens", 13], ["opa-locka", 13],
    ["west palm", 50], ["boca raton", 50], ["boynton", 50], ["delray", 50],
    ["jupiter", 50], ["lake worth", 50], ["palm beach", 50],
    ["wellington", 50], ["royal palm", 50],
    ["orlando", 48], ["kissimmee", 49], ["tampa", 29], ["st. petersburg", 52],
    ["st pete", 52], ["clearwater", 52], ["pinellas", 52],
    ["jacksonville", 16], ["duval", 16],
    ["naples", 11], ["collier", 11], ["fort myers", 36], ["cape coral", 36],
    ["lee county", 36], ["sarasota", 58], ["bradenton", 41], ["manatee", 41],
    ["daytona", 64], ["volusia", 64], ["gainesville", 1], ["alachua", 1],
    ["tallahassee", 37], ["leon", 37], ["pensacola", 17], ["escambia", 17],
    ["ocala", 42], ["marion", 42], ["lakeland", 53], ["polk", 53],
    ["brevard", 5], ["melbourne", 5], ["cocoa", 5],
    ["hillsborough", 29],
  ];
  for (const [keyword, code] of cityCountyMap) {
    if (lower.includes(keyword)) return code;
  }
  return null;
}

interface HistoricalTaxTabProps {
  dealId: string;
  propertyAddress?: string;
  existingAssessments?: TaxAssessment[];
  onTaxApplied?: () => void;
}

export default function HistoricalTaxTab({
  dealId,
  propertyAddress,
  existingAssessments,
  onTaxApplied,
}: HistoricalTaxTabProps) {
  const toast = useToast();
  const [mode, setMode] = useState<"manual" | "lookup">("manual");
  const [manualTax, setManualTax] = useState<string>("");
  const [counties, setCounties] = useState<CountyInfo[]>([]);
  const [countyCode, setCountyCode] = useState<number>(() => detectCountyFromAddress(propertyAddress) ?? 13);
  const [folios, setFolios] = useState<string[]>([""]);
  const [loading, setLoading] = useState(false);
  const [assessments, setAssessments] = useState<TaxAssessment[]>(
    existingAssessments || [],
  );
  const [notFound, setNotFound] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [millageRate, setMillageRate] = useState<string>("22.1847");
  const [applying, setApplying] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  async function handleApplyManual() {
    const amt = parseFloat(manualTax.replace(/[$,\s]/g, ""));
    if (isNaN(amt) || amt <= 0) {
      toast.error("Enter a positive annual tax amount.");
      return;
    }
    setApplying(true);
    try {
      await applyTaxToOS(dealId, "historical", amt);
      toast.success(`Applied ${fmtCurrency(amt)} to Operating Statement`);
      onTaxApplied?.();
    } catch (e: any) {
      toast.error(e?.message || "Couldn't apply that tax amount. Try again.");
    } finally {
      setApplying(false);
    }
  }

  const loadCounties = useCallback(async () => {
    try {
      const data = await fetchCounties(dealId);
      setCounties(data);
    } catch {
      /* county list is non-critical */
    }
  }, [dealId]);

  useEffect(() => {
    loadCounties();
  }, [loadCounties]);

  function addFolio() {
    setFolios((prev) => [...prev, ""]);
  }

  function removeFolio(index: number) {
    setFolios((prev) => prev.filter((_, i) => i !== index));
  }

  function updateFolio(index: number, value: string) {
    setFolios((prev) => prev.map((f, i) => (i === index ? value : f)));
  }

  async function handleFetch() {
    const validFolios = folios.map((f) => f.trim()).filter(Boolean);
    if (validFolios.length === 0) {
      toast.error("Enter at least one folio number.");
      return;
    }
    setLoading(true);
    setNotFound([]);
    setErrors([]);
    try {
      const result = await lookupTaxData(dealId, countyCode, validFolios);
      setAssessments(result.assessments);
      setNotFound(result.not_found);
      setErrors(result.errors);
      if (result.assessments.length > 0) {
        toast.success(
          `Found ${result.assessments.length} parcel${result.assessments.length > 1 ? "s" : ""}`,
        );
      }
    } catch (e: any) {
      toast.error(e?.message || "Failed to fetch tax data");
    } finally {
      setLoading(false);
    }
  }

  function computeEstimatedTax(assessment: TaxAssessment): number | null {
    const tv = assessment.taxable_value_nsd;
    const mills = parseFloat(millageRate);
    if (tv == null || isNaN(mills) || mills <= 0) return null;
    return tv * (mills / 1000);
  }

  function totalEstimatedTax(): number | null {
    const mills = parseFloat(millageRate);
    if (isNaN(mills) || mills <= 0) return null;
    let total = 0;
    for (const a of assessments) {
      const tv = a.taxable_value_nsd;
      if (tv == null) return null;
      total += tv * (mills / 1000);
    }
    return total;
  }

  async function handleApply() {
    const tax = totalEstimatedTax();
    if (tax == null || tax <= 0) {
      toast.error("Cannot apply: no valid tax amount calculated.");
      return;
    }
    setApplying(true);
    try {
      await applyTaxToOS(dealId, "historical", tax);
      toast.success(`Applied ${fmtCurrency(tax)} to Operating Statement`);
      onTaxApplied?.();
    } catch (e: any) {
      toast.error(e?.message || "Failed to apply tax");
    } finally {
      setApplying(false);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h3 className="font-display text-lg font-semibold text-bark-800">
          Insert Historical Taxes
        </h3>
        <p className="text-sm text-bark-400 mt-1">
          Type the annual tax in directly, or auto-fill from county records
          (Florida only).
        </p>
      </div>

      {/* Source toggle */}
      <div className="inline-flex rounded-lg border border-grove-300 bg-white p-1 text-sm">
        <button
          onClick={() => setMode("manual")}
          className={`px-4 py-1.5 rounded-md font-medium transition-colors ${
            mode === "manual"
              ? "bg-forest-700 text-white"
              : "text-bark-500 hover:text-bark-700"
          }`}
        >
          Enter manually
        </button>
        <button
          onClick={() => setMode("lookup")}
          className={`px-4 py-1.5 rounded-md font-medium transition-colors ${
            mode === "lookup"
              ? "bg-forest-700 text-white"
              : "text-bark-500 hover:text-bark-700"
          }`}
        >
          Auto-fill from county records
        </button>
      </div>

      {/* Manual entry form */}
      {mode === "manual" && (
        <div className="bg-white rounded-xl border border-grove-300 p-6 shadow-sm space-y-4">
          <div>
            <label className="text-sm font-medium text-bark-700 mb-1.5 block">
              Annual Property Tax
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={manualTax}
              onChange={(e) => setManualTax(e.target.value)}
              placeholder="e.g. 248,500"
              className="w-full max-w-xs px-3 py-2 border border-grove-300 rounded-lg text-sm text-bark-700 placeholder:text-bark-300 focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-forest-500 tabular-nums"
            />
            <p className="text-xs text-bark-400 mt-2">
              Enter the most recent annual tax bill. This applies directly to
              the Operating Statement.
            </p>
          </div>
          <button
            onClick={handleApplyManual}
            disabled={applying || !manualTax.trim()}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 transition-all disabled:opacity-50"
          >
            <CheckCircle className="w-4 h-4" />
            {applying ? "Applying..." : "Use This Tax Amount in Operating Statement"}
          </button>
        </div>
      )}

      {/* Lookup form */}
      {mode === "lookup" && (
      <div className="bg-white rounded-xl border border-grove-300 p-6 shadow-sm space-y-4">
        {/* County dropdown */}
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <label className="text-sm font-medium text-bark-700">County</label>
            <div className="relative">
              <button
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                className="text-bark-300 hover:text-bark-500"
              >
                <Info className="w-3.5 h-3.5" />
              </button>
              {showTooltip && (
                <div className="absolute left-6 top-0 z-20 w-64 bg-bark-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
                  All 67 Florida counties are covered via the statewide FDOR
                  database. If your property is outside Florida, enter taxes
                  manually on the Operating Statement tab.
                </div>
              )}
            </div>
          </div>
          <select
            value={countyCode}
            onChange={(e) => setCountyCode(parseInt(e.target.value))}
            className="w-full max-w-md px-3 py-2 border border-grove-300 rounded-lg text-sm text-bark-700 bg-white focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
          >
            {counties.length > 0
              ? counties.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} ({c.code})
                  </option>
                ))
              : Object.entries({
                  13: "Miami-Dade",
                  6: "Broward",
                  50: "Palm Beach",
                }).map(([code, name]) => (
                  <option key={code} value={code}>
                    {name} ({code})
                  </option>
                ))}
          </select>
        </div>

        {/* Folio inputs */}
        <div>
          <label className="text-sm font-medium text-bark-700 mb-1.5 block">
            Folio Number(s)
          </label>
          {folios.map((folio, idx) => (
            <div key={idx} className="flex items-center gap-2 mb-2">
              <input
                type="text"
                value={folio}
                onChange={(e) => updateFolio(idx, e.target.value)}
                placeholder="e.g. 30-3920-003-0010"
                className="flex-1 max-w-md px-3 py-2 border border-grove-300 rounded-lg text-sm text-bark-700 placeholder:text-bark-300 focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
              />
              {folios.length > 1 && (
                <button
                  onClick={() => removeFolio(idx)}
                  className="text-bark-400 hover:text-red-500 text-sm"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            onClick={addFolio}
            className="text-sm text-forest-600 hover:text-forest-800 font-medium"
          >
            + Add Another Folio
          </button>
        </div>

        {/* Fetch button */}
        <div>
          <button
            onClick={handleFetch}
            disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-forest-700 text-white text-sm font-semibold rounded-lg hover:bg-forest-600 transition-all disabled:opacity-50"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            {loading ? "Fetching..." : "Fetch Tax Data"}
          </button>
        </div>
      </div>
      )}

      {/* Not found warnings */}
      {mode === "lookup" && notFound.length > 0 && (
        <div className="bg-honey-50 border border-honey-200 rounded-xl px-5 py-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-honey-600 mt-0.5 shrink-0" />
          <div className="text-sm text-honey-800">
            {notFound.length === 1
              ? `Folio ${notFound[0]} was not found in county records. Check the folio format.`
              : `${notFound.length} folios not found: ${notFound.join(", ")}. Check the folio format.`}
          </div>
        </div>
      )}

      {/* Error messages */}
      {mode === "lookup" && errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3 space-y-1">
          {errors.map((err, i) => (
            <div key={i} className="text-sm text-red-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              {err}
            </div>
          ))}
        </div>
      )}

      {/* Results */}
      {mode === "lookup" && assessments.map((a, idx) => (
        <TaxAssessmentCard key={idx} assessment={a} />
      ))}

      {/* Tax estimate section */}
      {mode === "lookup" && assessments.length > 0 && (
        <div className="bg-white rounded-xl border border-grove-300 shadow-sm p-6 space-y-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-bark-400">
            Tax Estimate
          </h4>
          <div className="flex items-end gap-4">
            <div>
              <label className="text-sm font-medium text-bark-700 block mb-1.5">
                Millage Rate (mills)
              </label>
              <input
                type="number"
                step="0.0001"
                value={millageRate}
                onChange={(e) => setMillageRate(e.target.value)}
                className="w-40 px-3 py-2 border border-grove-300 rounded-lg text-sm text-bark-700 focus:outline-none focus:ring-2 focus:ring-forest-500"
              />
            </div>
            <div className="text-sm text-bark-500">
              Not in FDOR data -- enter manually from county tax collector
            </div>
          </div>

          {/* Per-parcel estimates */}
          {assessments.length > 1 && (
            <div className="space-y-1">
              {assessments.map((a, idx) => {
                const est = computeEstimatedTax(a);
                return (
                  <div
                    key={idx}
                    className="flex justify-between text-sm text-bark-600 py-1"
                  >
                    <span>{a.folio_number}</span>
                    <span className="tabular-nums">
                      {est != null ? fmtCurrency(est) : "--"}
                    </span>
                  </div>
                );
              })}
              <div className="border-t border-grove-200 pt-2 mt-2" />
            </div>
          )}

          {/* Total estimated tax */}
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold text-bark-800">
              Estimated Annual Tax
            </span>
            <span className="text-lg font-bold text-forest-700 tabular-nums">
              {totalEstimatedTax() != null
                ? `${fmtCurrency(totalEstimatedTax()!)} /yr`
                : "--"}
            </span>
          </div>

          {/* Apply button */}
          <button
            onClick={handleApply}
            disabled={applying || totalEstimatedTax() == null}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 transition-all disabled:opacity-50"
          >
            <CheckCircle className="w-4 h-4" />
            {applying
              ? "Applying..."
              : "Use This Tax Amount in Operating Statement"}
          </button>

          {/* Metadata */}
          {assessments[0]?.fetched_at && (
            <div className="text-xs text-bark-400 mt-2">
              Data source: FDOR Statewide Cadastral &middot; Last fetched:{" "}
              {new Date(assessments[0].fetched_at).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
